/* eslint-disable react-refresh/only-export-components -- the co-located parts
   of EntryPage: its extracted hooks and sub-components share one module by
   design, and this is not a route or fast-refresh boundary. */
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";

import { HomeBack, HomeLogo } from "./HomeLinks";
import { HomeAccountMenu } from "./HomeAccountMenu";
import { ensureCampChoice } from "@/components/room/campIdentity";
import { Flame, LogIn } from "lucide-react";

import {
  useDisplayName,
  useIsDisplayNameCustom,
  setSuggestedDisplayName,
} from "@/hooks/useDisplayName";
import { useAvatarChoice } from "@/hooks/useAvatarChoice";
import {
  resolveAvatarColor,
  resolveAvatarEmoji,
} from "@/engine/state/avatarChoice";
import type { RoomPeek } from "@/engine/state/roomPeek";
import type { SavedRoom } from "@/engine/state/savedRooms";
import { useClientValue } from "@/hooks/useClientValue";
import { useHydrated } from "@/hooks/useHydrated";
import { useHasRail } from "@/hooks/useHasRail";
import { type EntryRefusal } from "@/engine/state/roomAccess";
import { suggestNickname } from "@/engine/state/nickname";
import { extractRoomCode, getSessionPeerId } from "@/engine/state/roomIdentity";

import { cn } from "@/engine/support/classNames";
import { EXPLAIN_AFTER_MS } from "@/engine/diagnostics/entryTiming";
import type { EntryWalkIn } from "@/hooks/useRoomEntry";
export interface EntryPageProps {
  /** The door home opens on, where a sign-in round trip names one. */
  opensOn?: "create" | undefined;
  /** Create pressed: the room is written, then its threshold is shown. */
  onCreateRoom: (name?: string) => void;
  /** Connect, or a saved room, pressed: that room's threshold is shown. */
  onJoinRoom: (code: string) => void;
  /** A phone handed this computer a room: its threshold, saying so (#864). */
  onHandedOver?: (code: string) => void;
  /**
   * Pressing the way in with no code typed. The demo is an address of this
   * browser's own rather than a room on the server (#656), so what happens is
   * a navigation and the screen above, which is inside the router, does it.
   */
  onTryDemo: () => void;
  /**
   * The rooms this visitor can go back to, for the rail.
   *
   * Handed down rather than read here. The room screen draws the same rail from
   * the same list, and two screens asking for it apart would be two lists, two
   * reads of the account's rooms, and two looks at every room on them — which
   * the client answers by handing both the same channel.
   */
  savedRooms?: readonly SavedRoom[];
  /** How many are standing in each of the few that were looked at. */
  roomCounts?: Record<string, number>;
  /** Gives one of them up for good. True where the room is gone. */
  onForgetRoom?: (code: string) => Promise<boolean>;
  /** Abandons a walk in that is taking too long. */
  onCancelEntry?: () => void;
  isConnecting?: boolean;
  /** The leaving screen remains mounted while the room fades in. */
  peekEnabled?: boolean;
  prefillCode?: string;
  /** Why the last join was refused, when it was. */
  entryError?: EntryRefusal | null;
  /** Set while a walk into a room is going on, and how it began. */
  walkIn?: EntryWalkIn | null;
  selectedMicId: string | null;
  selectedCameraId?: string | null;
  onMicSelect: (id: string) => void;
  onCameraSelect?: (id: string) => void;
}

/**
 * The way in, at both doors.
 *
 * One height, and the phone's is no longer the smallest of three: the way in is
 * the object a thumb is aimed at, and a phone is where the aiming is hardest.
 * The ladder that made it 44px there and 56px on a mouse-driven screen had it
 * exactly backwards.
 */

const getRoomCodeFromLocation = (): string => {
  if (typeof window === "undefined") return "";
  const fromSearch = new URLSearchParams(window.location.search).get("room");
  return (fromSearch || "").trim().toLowerCase();
};

/**
 * The generated nickname, and the effects that keep it current: the visitor's
 * first suggestion becomes their own avatar, and stands in as the default name
 * until they type one.
 */
function useSuggestedName(displayName: string, nameIsCustom: boolean): string {
  // The peer id lives in sessionStorage, so the server cannot know it and a
  // render that guessed one made the hydrated markup disagree with the HTML.
  const hydrated = useHydrated();
  const avatarChoice = useAvatarChoice();

  useEffect(() => {
    if (hydrated) ensureCampChoice(getSessionPeerId());
  }, [hydrated]);

  const suggestedName = hydrated
    ? suggestNickname(getSessionPeerId(), avatarChoice)
    : "";

  useEffect(() => {
    if (!suggestedName) return;
    // A name the visitor typed is theirs. One the screen suggested follows the
    // character, so a shuffled animal does not keep the old animal's name.
    if (nameIsCustom || displayName === suggestedName) return;
    setSuggestedDisplayName(suggestedName);
  }, [displayName, nameIsCustom, suggestedName]);

  return suggestedName;
}

/**
 * The room code, with the invitation (`?room=` or `prefillCode`) as the value
 * until the visitor types their own.
 */
function useInvitedRoomCode(prefillCode: string) {
  // The server has no `?room=`, so the invitation is only known once the
  // client has hydrated; until then both sides render the empty entry screen.
  const urlRoomCode = useClientValue(getRoomCodeFromLocation, "");
  const invitedCode = prefillCode || urlRoomCode;
  const [typedRoomCode, setRoomCode] = useState<string | null>(
    prefillCode ? prefillCode : null,
  );
  const roomCode = typedRoomCode ?? invitedCode;
  const joinsDemoRoom = !roomCode.trim();
  return {
    invitedCode,
    roomCode,
    setRoomCode,
    joinCode: extractRoomCode(roomCode),
    // An empty code is not a mistake to be scolded for: it is a visitor with no
    // invitation, and the demo is the answer to that. It used to be a room on
    // the server and is an address of this browser's own now (#656), so what
    // the press opens is a route rather than a code.
    joinsDemoRoom,
    codeToJoin: extractRoomCode(roomCode),
  };
}

/**
 * The name complaint, tied to the name it was raised about so typing clears it
 * without an effect, plus the 2-character floor.
 */
function useNameError(displayName: string, effectiveName: string) {
  const [raised, setRaised] = useState<{
    name: string;
    message: string;
  } | null>(null);
  const nameError = raised?.name === displayName ? raised.message : null;
  const setNameError = (message: string | null) =>
    setRaised(message ? { name: displayName, message } : null);
  const validate = (): string | null => {
    if (!effectiveName) return "Enter your name to join the room";
    if (effectiveName.length < 2) return "Name must be at least 2 characters";
    return null;
  };
  return { nameError, setNameError, validate };
}

/**
 * The card's own height, and the space kept for it.
 *
 * Two numbers, because they answer different questions. The card animates to
 * the panel on show. The space around it holds the tallest panel the card has
 * had to draw and never gives that space back.
 *
 * Without the second the card's height *is* the column's height, and the column
 * is centred in the window — so a shorter panel takes half its difference off
 * the top and the scene, the avatar and the doors all slide up at the instant
 * of the press, before the card has begun to move. That is the jump. A card
 * resizing inside a box that does not is one movement; a card that resizes and
 * drags the screen with it is two, in opposite directions.
 *
 * The reservation is dropped on a resize. It is a number of pixels and the
 * panels are sized in breakpoints, so a measurement from a wider window is not
 * an answer about this one.
 */
function useCardHeightAnimation(activeTab: string) {
  const measured = useRef<HTMLDivElement | null>(null);
  const watcher = useRef<ResizeObserver | null>(null);
  const [cardHeight, setCardHeight] = useState<number | undefined>(undefined);
  const [reservedHeight, setReservedHeight] = useState<number | undefined>(
    undefined,
  );

  const read = useCallback((el: HTMLDivElement) => {
    const height = el.offsetHeight;
    // Nought is not a height. An element that has been taken out of the
    // document — which is what happens the moment the screen decides it has
    // room for a frame and rebuilds the tree inside one — measures nought on
    // its way out, and taking that for an answer collapses the card.
    if (height <= 0) return;
    setCardHeight(height);
    setReservedHeight((tallest) =>
      tallest === undefined || height > tallest ? height : tallest,
    );
  }, []);

  /**
   * A callback ref rather than an effect, because the node does not live as
   * long as the component. The card is rebuilt whenever the screen moves
   * between the frame and the bare card, and an observer attached once at
   * mount goes on watching the node that was thrown away.
   */
  const cardInnerRef = useCallback(
    (el: HTMLDivElement | null) => {
      watcher.current?.disconnect();
      watcher.current = null;
      measured.current = el;
      if (!el) return;
      read(el);
      const observer = new ResizeObserver(() => read(el));
      observer.observe(el);
      watcher.current = observer;
    },
    [read],
  );

  useEffect(() => {
    const el = measured.current;
    if (el) read(el);
  }, [activeTab, read]);

  useEffect(() => {
    const forget = () => {
      const el = measured.current;
      if (el && el.offsetHeight > 0) setReservedHeight(el.offsetHeight);
    };
    window.addEventListener("resize", forget);
    return () => window.removeEventListener("resize", forget);
  }, []);

  return { cardInnerRef, cardHeight, reservedHeight };
}

/**
 * Whether the waits should have replaced the door by now.
 *
 * A press is answered in a few milliseconds far more often than not, and a
 * panel that appeared and vanished inside that reads as a fault rather than as
 * progress — so the door keeps its place for `EXPLAIN_AFTER_MS` first. A
 * visitor coming back from a sign-in has no door to keep: they arrive on a page
 * that remembers nothing, and the waits are the whole screen from the first
 * frame.
 */
function useWaitsAreWorthDrawing(walkIn: EntryWalkIn | null): boolean {
  const [patienceSpent, setPatienceSpent] = useState(false);

  useEffect(() => {
    if (walkIn !== "pressed") return;
    const id = window.setTimeout(
      () => setPatienceSpent(true),
      EXPLAIN_AFTER_MS,
    );
    // Cleared on the way out rather than on the way in: each walk in spends its
    // own patience, and setting state in the body of an effect is a render the
    // effect causes itself.
    return () => {
      window.clearTimeout(id);
      setPatienceSpent(false);
    };
  }, [walkIn]);

  return walkIn === "resumed" || (walkIn === "pressed" && patienceSpent);
}

/** The two ways in. Ordered as they are shown, which is what the mark moves along. */
export const ENTRY_TABS = ["create", "join"] as const;
export type EntryTab = (typeof ENTRY_TABS)[number];

/**
 * The way to the other door — and only the other one.
 *
 * The door already standing open is not drawn again. Pressing the door you are
 * already standing in moves nothing, and a control that moves nothing is read
 * as a broken button, not as a tab that happens to be open: on home the code
 * field is already there under the buttons, so the only press worth drawing is
 * the one that leaves it. Standing on the create side, the same rule draws the
 * way back to the code field.
 *
 * Each door keeps the skin it had in the pair: lighting a fire is the call and
 * wears the action colour, while the way back to joining by code is a quiet
 * bordered line.
 */
export function EntryDoors({
  activeTab,
  onChoose,
  disabled,
  oneWayIn,
}: {
  activeTab: EntryTab;
  onChoose: (tab: EntryTab) => void;
  /** True while a walk in is going on: the door is not a thing to change then. */
  disabled: boolean;
  /** True where an invitation makes the way across a fork nobody asked for. */
  oneWayIn: boolean;
}) {
  const { t } = useTranslation();
  const other: EntryTab = activeTab === "join" ? "create" : "join";
  return (
    <div
      data-testid="entry-tabs"
      className={cn(
        "mb-3 sm:mb-4",
        // A width rule rather than not rendering it: a wide screen keeps the
        // way across however the visitor arrived, and the markup is the same
        // on both sides of hydration.
        oneWayIn && "hidden sm:block",
      )}
      style={{ animationDelay: "0.2s" }}
    >
      {other === "create" ? (
        <button
          type="button"
          data-testid="entry-tab-create"
          disabled={disabled}
          onClick={() => onChoose("create")}
          className="flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-primary px-3 text-base font-semibold text-primary-foreground shadow-glow-sm transition-colors duration-(--motion-base) hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        >
          <Flame aria-hidden="true" className="size-5" />
          {t("entry.createRoom")}
        </button>
      ) : (
        <button
          type="button"
          data-testid="entry-tab-join"
          disabled={disabled}
          onClick={() => onChoose("join")}
          className="flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl border border-border bg-card px-3 text-sm font-semibold text-muted-foreground transition-colors duration-(--motion-base) hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 sm:text-base"
        >
          <LogIn aria-hidden="true" className="size-4" />
          {t("entry.join")}
        </button>
      )}
    </div>
  );
}

/**
 * Which door is open, and whether a door is worth drawing at all.
 *
 * An invitation opens the join door until the visitor picks one themselves.
 * Somebody who followed a link is not choosing between joining and creating —
 * they were invited somewhere — so until they say otherwise the door is a fork
 * for the rarer case, and a phone pays for it in the width the way in needs.
 */
export function useEntryDoor(
  invitedCode: string,
  account: { known: boolean; signedIn: boolean } = {
    known: false,
    signedIn: false,
  },
  opensOn?: EntryTab,
) {
  const [chosen, setChosen] = useState<EntryTab | null>(null);
  // Home opens on Join for everybody (#738): a guest's way in is somebody
  // else's room, and a signed-in visitor's home is their rooms, from which
  // opening a new one is a press on Create rather than the page they land on.
  const offered: EntryTab = opensOn ?? "join";
  return {
    activeTab: chosen ?? offered,
    setActiveTab: setChosen,
    oneWayIn: Boolean(invitedCode) && chosen === null,
    /** The door is the visitor's own choice rather than the one offered. */
    chosenByVisitor: chosen !== null,
    /**
     * A first visit (#708): drawn from the first paint until the account says
     * otherwise, so that learning it moves nothing (#872). While it is not yet
     * known, a session stored in this browser hides it — see `provisional`.
     */
    firstVisit: !account.signedIn,
    /** The account has not answered: the first visit is drawn on a guess. */
    provisional: !account.known,
  } as const;
}

/**
 * What the room-instead-of-a-form state needs, and whether it is that state.
 *
 * Only where a peek has answered: there is nothing to draw a room from
 * otherwise, and the book rules out a shell with placeholders where people
 * should be. Only once hydrated, because the visitor's own animal is keyed to a
 * session id the server cannot know — and a peek cannot have answered before
 * then anyway.
 *
 * And only where the window has room for it. §11 folds the two states into one
 * on a phone — the strip, then the card — and a shell with a rail down each
 * side squeezed into a 448px column is not a smaller version of the room, it is
 * an unreadable one.
 */
export function useInvitedRoomPreview(
  door: EntryTab,
  peek: RoomPeek,
  walking: boolean,
) {
  const hydrated = useHydrated();
  const roomForShell = useHasRail();
  const choice = useAvatarChoice();
  const peerId = hydrated ? getSessionPeerId() : "";
  return {
    /** True where a shell has room to be drawn beside what it surrounds. */
    roomForShell,
    // A room that answered with nobody in it is not a room to draw. The whole
    // reason to draw one instead of a form is that the peek came back holding
    // something a form cannot say — who is already there. A count of nought
    // says nothing the strip above the card does not say better, and it buys
    // that with a page of empty ground and one dashed square. Worse, it makes
    // the same link open two different screens depending on whether the peek
    // won its race, which is what a visitor reads as a fault.
    showsRoom:
      hydrated &&
      roomForShell &&
      door === "join" &&
      peek.kind === "answered" &&
      peek.count > 0 &&
      !walking,
    avatar: {
      emoji: resolveAvatarEmoji(peerId, choice.emoji),
      color: resolveAvatarColor(peerId, choice.color),
    },
  };
}

/** The complaint about a name, in the one shape both states draw it in. */
/**
 * Whether a change of panel goes without a transition: the door was offered
 * rather than chosen, and nobody is being walked in — so the change is the
 * account's answer arriving, not a press.
 */
export const panelChangesQuietly = (
  chosenByVisitor: boolean,
  walking: boolean,
) => !chosenByVisitor && !walking;

export function NameComplaint({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p
      data-testid="entry-name-error"
      role="alert"
      className="text-center text-xs sm:text-sm text-destructive"
    >
      {message}
    </p>
  );
}

/** The visitor's identity state — display name, its generated default, errors. */
export function useEntryIdentity() {
  const displayName = useDisplayName();
  const nameIsCustom = useIsDisplayNameCustom();
  const suggestedName = useSuggestedName(displayName, nameIsCustom);
  // A nameless participant enters under the suggested nickname, which becomes
  // their real display name on submit.
  const effectiveName = displayName.trim() || suggestedName;
  const { nameError, setNameError, validate } = useNameError(
    displayName,
    effectiveName,
  );
  return { effectiveName, nameError, setNameError, validate };
}

export { useInvitedRoomCode, useCardHeightAnimation };

type TopBarProps = Pick<
  EntryPageProps,
  "selectedMicId" | "selectedCameraId" | "onMicSelect" | "onCameraSelect"
> & {
  /** The code in hand, if any: what signing in would come back to do. */
  joinCode: string;
  /**
   * Where Back leads from a screen under home: a step back, or home itself.
   * Absent on home, which has nowhere further back to go.
   */
  back?: "home" | (() => void) | undefined;
};

/**
 * The shared top bar: the logo on the left, and on the right the one menu that
 * holds everything about the visitor (#821).
 */
export function EntryTopBar({
  selectedMicId,
  selectedCameraId,
  onMicSelect,
  onCameraSelect,
  joinCode,
  back,
}: TopBarProps) {
  return (
    <div className="absolute top-0 inset-x-0 z-20 h-12 px-2 sm:px-4 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <HomeLogo />
        {back && <HomeBack onBack={back === "home" ? undefined : back} />}
      </div>
      {/* Signing in comes back to the room the visitor was heading for, when
          they were heading for one. */}
      <HomeAccountMenu
        intent={joinCode ? { kind: "join", code: joinCode } : null}
        selectedAudioInput={selectedMicId || undefined}
        selectedVideoInput={selectedCameraId || undefined}
        onAudioInputChange={onMicSelect}
        onVideoInputChange={onCameraSelect}
      />
    </div>
  );
}

/**
 * Create tab: the host-only mix chooser (signed in), a sign-in nudge (account
 * known but signed out) or the Create button while the account is still
 * unknown or the request is in flight.
 */

/** A tab, or the visitor being walked in: one sequence of panels in one card. */
export type EntryPanel = EntryTab | "entering";

/**
 * Which panel the card shows, and whether its door is busy.
 *
 * The three answers are one fact seen from three sides, so they are computed
 * together: a door that looks idle while the walk in runs invites a second
 * press, and a panel that has moved on while the door still looks pressed is
 * the same disagreement from the other end.
 */
export function useEntryPanel(
  activeTab: EntryTab,
  walkIn: EntryWalkIn | null | undefined,
  isConnecting: boolean | undefined,
) {
  const walking = useWaitsAreWorthDrawing(walkIn ?? null);
  const panel: EntryPanel = walking ? "entering" : activeTab;
  const busy = Boolean(isConnecting) || Boolean(walkIn);
  return { walking, panel, busy };
}

/**
 * What the middle of the frame owns.
 *
 * `asPage` is the create screen with an account behind it — it wants the shell
 * at its drawn width rather than a card. Either that or an invited room owns
 * the whole middle, and everything else is a card inside it.
 */
export function entryShell(
  panel: EntryPanel,
  accountKnown: boolean,
  signedIn: boolean,
  showsRoom: boolean,
) {
  // The four pictures are the screen's whole argument, and the surface holding
  // them widens wherever it is holding them — the grid folds itself on
  // whatever width that turns out to be. Home itself is a column at every
  // width (#738): only a screen that owns its whole area is drawn in the frame.
  const asPage = panel === "create" && accountKnown && signedIn;
  const onTheShell = asPage || showsRoom;
  return { asPage, onTheShell, wide: onTheShell };
}

/**
 * The code the join door is looking behind, or nothing where it is not looking.
 *
 * Looking at a door is not going through it: an empty code is what stops the
 * peek, so every state that is not the join door standing open reports one.
 */
export function peekedCode(
  panel: EntryPanel,
  peekEnabled: boolean | undefined,
  joinCode: string,
) {
  return panel === "join" && peekEnabled !== false ? joinCode : "";
}
